import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import Button from "components/buttons/Button/Button";
import { t } from "i18next";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import { handleApiError } from "~/api/axiosUtils.ts";
import CompanyLogo from "~/components/CompanyLogo";
import SubmitButton from "~/components/formElements/SubmitButton";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { useAllCompanyAvatarImages } from "~/modules/image/api/avatarImage/avatarImageQueries.ts";
import type { Location, UserAvailableLocationType } from "~/modules/location/api/location/locationTypes.ts";
import { orderQueries } from "~/modules/project/api/order/orderQueries.ts";
import { ALL_PROJECTS_QUERY_KEY } from "~/modules/project/api/project/projectQueries.ts";
import { PROJECTS_PHASES_QUERY_KEY } from "~/modules/project/api/projectPhase/projectPhaseQueries.ts";
import {
	PROJECTS_STAFFINGS_QUERY_KEY,
	USERS_ACTIVE_STAFFINGS_QUERY_KEY,
} from "~/modules/project/api/staffing/staffingQueries.ts";
import type { StaffingTimeTrackingSummary } from "~/modules/project/api/staffing/staffingTypes.ts";
import { quotaQueries } from "~/modules/quota/api/quota/quotaQueries.ts";
import type { Quota } from "~/modules/quota/api/quota/quotaTypes.ts";
import type { UserAvailableDeliverableType } from "~/modules/timeTracking/api/deliverable/deliverableTypes.ts";
import {
	createTimeTracking,
	updateTimeTracking,
} from "~/modules/timeTracking/api/timeTracking/timeTrackingApiDispatchers.ts";
import {
	PROJECTS_TIME_TRACKINGS_QUERY_KEY,
	TIME_TRACKINGS_BY_USER_QUERY_KEY,
} from "~/modules/timeTracking/api/timeTracking/timeTrackingQueries.ts";
import type {
	TimeTrackingFormData,
	TimeTrackingQuotaLock,
} from "~/modules/timeTracking/api/timeTracking/timeTrackingTypes.ts";
import { TimeTrackingTypeId } from "~/modules/timeTracking/api/timeTrackingType/timeTrackingTypeTypes.ts";
import {
	createTimeTrackingFormDefaultValues,
} from "~/modules/timeTracking/components/forms/CreateTimeTrackingForm/createTimeTrackingFormDefaultValues.ts";
import TimeTrackingFormSection
	from "~/modules/timeTracking/components/forms/formSections/TimeTrackingFormSection/TimeTrackingFormSection";
import { TimeTrackingFormNamesEnum } from "~/modules/timeTracking/types/timeTrackingTypes.ts";
import type { CurrentQuotaBooking } from "~/modules/timeTracking/utils/timeTrackingUtils.ts";
import {
	getQuotaBookingOptions,
	getQuotaDrawCents,
	getStartAndEndOfMonthFormatted,
} from "~/modules/timeTracking/utils/timeTrackingUtils.ts";
import type { User } from "~/modules/user/api/user/userTypes.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { formatDateToYYYYMMDD, formatDateWithGermanMonth } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type CreateTimeTrackingFormProps = {
	availableDeliverables: UserAvailableDeliverableType[];
	availableLocations: UserAvailableLocationType[];
	currentQuotaBooking?: CurrentQuotaBooking | null;
	date: Date | null,
	initialValues: TimeTrackingFormData;
	timeTrackingId?: string;
	locations: Location[];
	parentIsVisible: boolean;
	// Set on an update of a tracking whose quota no longer takes its bookings. The save is blocked then.
	quotaLock?: TimeTrackingQuotaLock | null;
	quotas: Record<string, Quota>;
	onSuccess: () => void;
	setCurrentTimeTrackingFormValues: (values: TimeTrackingFormData) => void;
	selectedStaffingData: StaffingTimeTrackingSummary | null;
	selectedStaffingId: string | null;
	selectedTimeTrackingTypeId: string;
	selectedTimeTrackingTypeName: string;
	selectedUserId: User["id"];
	setCurrentFormName: (formId: TimeTrackingFormNamesEnum | null) => void;
	users: User[];
};

const minutesMinMaxErrorMessage = "Bitte einen Wert zwischen 0 und 59 eingeben";
const hoursMinMaxErrorMessage = "Bitte einen Wert zwischen 0 und 24 eingeben";
const staffingErrorMessagesByRule: Record<string, string> = {
	timeTrackingDateInsideStaffingPeriodRule: "Der Tag der Erfassung liegt außerhalb des Staffing-Zeitraums.",
};


const CreateOrUpdateTimeTrackingForm: React.FunctionComponent<CreateTimeTrackingFormProps> = ({
	availableDeliverables,
	availableLocations,
	currentQuotaBooking = null,
	date,
	initialValues,
	timeTrackingId,
	locations,
	onSuccess,
	parentIsVisible,
	quotaLock = null,
	quotas,
	setCurrentTimeTrackingFormValues,
	selectedStaffingData,
	selectedStaffingId,
	selectedTimeTrackingTypeId,
	selectedTimeTrackingTypeName,
	selectedUserId,
	setCurrentFormName,
	users,
}) => {
	const queryClient = useQueryClient();
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState<string>("");
	// The backend refuses the staffing of the tracking, e.g. for a date outside the staffing period.
	const [staffingErrorMessage, setStaffingErrorMessage] = useState<string>("");

	const isUpdateForm = timeTrackingId !== undefined;

	const quotaBookingOptions = useMemo(() => {
		if (quotaLock || selectedTimeTrackingTypeId !== TimeTrackingTypeId.Project || !selectedStaffingData?.isBillable) {
			return [];
		}

		return getQuotaBookingOptions({ currentQuotaBooking, date, quotas, staffing: selectedStaffingData });
	}, [currentQuotaBooking, date, quotaLock, quotas, selectedStaffingData, selectedTimeTrackingTypeId]);

	// A new tracking books to the quota by default when exactly one quota is eligible, the quota asks for it,
	// and the quota has budget left. Without budget each booking is blocked, so the switch stays off.
	const bookOnQuotaByDefault = quotaBookingOptions.length === 1
		&& quotaBookingOptions[0].quota.preselectForTimeTracking
		&& quotaBookingOptions[0].availableCents > 0;

	const defaultValues = useMemo(() => {
		if (selectedStaffingData && !isUpdateForm) {
			return {
				...initialValues,
				isBillable: selectedStaffingData.isBillable,
				bookOnQuota: bookOnQuotaByDefault,
			};
		}
		return initialValues;
	}, [bookOnQuotaByDefault, initialValues, isUpdateForm, selectedStaffingData]);


	const schema = useMemo(() => {
		const textSchema = selectedTimeTrackingTypeId === TimeTrackingTypeId.Project ? yup.string().required() : yup.string().nullable().default(null);
		const locationSchema = selectedTimeTrackingTypeId !== TimeTrackingTypeId.PartTime ? yup.string().required() : yup.string().nullable().default(null);
		return yup.object({
			hours: yup
				.number()
				.min(0, hoursMinMaxErrorMessage)
				.max(24, hoursMinMaxErrorMessage)
				.required()
				.test("minTime", "Bitte gib einen Wert ein.", function(value) {
					return !(value === 0 && this.parent.minutes === 0);
				}),
			isBillable: yup.boolean().required(),
			bookOnQuota: yup.boolean().required(),
			minutes: yup.number().min(0, minutesMinMaxErrorMessage).max(59, minutesMinMaxErrorMessage).required(),
			locationName: locationSchema,
			text: textSchema,
		});
	}, [selectedTimeTrackingTypeId]);

	const { data: allCompanyAvatarImages } = useAllCompanyAvatarImages();
	const avatarImage = TimeTrackingTypeId.Project && selectedStaffingData && allCompanyAvatarImages?.find((image) => image.clientIds.includes(selectedStaffingData?.clientId));

	const {
		handleSubmit,
		control,
		watch,
		setValue,
		getValues,
		formState: { errors, isValid, isSubmitted },
		trigger,
		reset,
	} = useForm<TimeTrackingFormData>({
		defaultValues,
		mode: "onChange",
		resolver: yupResolver<TimeTrackingFormData>(schema),
	});

	useEffect(() => {
		if (!parentIsVisible) {
			reset(createTimeTrackingFormDefaultValues);
		}
	}, [parentIsVisible, reset]);

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues, parentIsVisible, reset]);

	const [watchedHours, watchedMinutes, watchedBookOnQuota] = watch(["hours", "minutes", "bookOnQuota"]);
	const quotaDrawCents = selectedStaffingData
		? getQuotaDrawCents(watchedHours * 60 + watchedMinutes, selectedStaffingData.dailyRateCents)
		: 0;
	// The backend refuses a booking above the available amount, so the form blocks the save first.
	const quotaBudgetIsExceeded = watchedBookOnQuota
		&& quotaBookingOptions.length === 1
		&& quotaDrawCents > quotaBookingOptions[0].availableCents;

	const onSubmit: SubmitHandler<TimeTrackingFormData> = async (data) => {
		if (quotaLock || quotaBudgetIsExceeded) {
			return;
		}

		setIsBusy(true);
		setStaffingErrorMessage("");
		const timeTrackingDate = formatDateToYYYYMMDD(date!);
		// With two or more eligible quotas the switch is disabled and the tracking books to normal invoicing.
		const bookOnQuota = data.bookOnQuota && data.isBillable && quotaBookingOptions.length === 1;
		const timeTrackingBaseData = {
			isBillable: data.isBillable,
			locationName: data.locationName || null,
			quotaId: bookOnQuota ? quotaBookingOptions[0].quota.id : null,
			staffingId: selectedStaffingId,
			timeTrackingTypeId: selectedTimeTrackingTypeId!,
			minutes: data.hours * 60 + data.minutes,
			userId: selectedUserId,
			text: data.text || null,
		};

		if (selectedTimeTrackingTypeId === TimeTrackingTypeId.PartTime) {
			timeTrackingBaseData.locationName = null;
			timeTrackingBaseData.text = null;
		}

		if (selectedTimeTrackingTypeId !== TimeTrackingTypeId.Project) {
			timeTrackingBaseData.staffingId = null;
		}

		try {
			if (!isUpdateForm) {
				const createTimeTrackingData = {
					...timeTrackingBaseData,
					date: timeTrackingDate,
				};

				await createTimeTracking(selectedUserId, createTimeTrackingData);
			} else {
				await updateTimeTracking({ userId: selectedUserId, timeTrackingId, data: timeTrackingBaseData });
			}


			const timeTrackingFilterData = getStartAndEndOfMonthFormatted(new Date(timeTrackingDate));
			await queryClient.refetchQueries({ queryKey: TIME_TRACKINGS_BY_USER_QUERY_KEY(selectedUserId, timeTrackingFilterData) });
			await queryClient.refetchQueries({ queryKey: USERS_ACTIVE_STAFFINGS_QUERY_KEY(selectedUserId) });
			queryClient.invalidateQueries({ queryKey: ALL_PROJECTS_QUERY_KEY });
			if (selectedStaffingData) {
				queryClient.invalidateQueries({ queryKey: PROJECTS_TIME_TRACKINGS_QUERY_KEY(selectedStaffingData.projectId) });
				queryClient.invalidateQueries({ queryKey: PROJECTS_STAFFINGS_QUERY_KEY(selectedStaffingData.projectId) });
				queryClient.invalidateQueries({ queryKey: orderQueries.byProject(selectedStaffingData.projectId).queryKey });
				queryClient.invalidateQueries({ queryKey: PROJECTS_PHASES_QUERY_KEY(selectedStaffingData.projectId) });
			}
			if(timeTrackingBaseData.quotaId ) {
				queryClient.invalidateQueries({ queryKey: [...quotaQueries.baseKey, "details", timeTrackingBaseData.quotaId]});
			}

			if(currentQuotaBooking?.quotaId){
				queryClient.invalidateQueries({ queryKey: [...quotaQueries.baseKey, "details", currentQuotaBooking.quotaId]});
			}
			onSuccess();
			reset();
		} catch (error) {
			const apiError = handleApiError(error);
			const staffingError = apiError.type === ApiResponseErrorType.VALIDATION
				? apiError.messages.staffingId?.[0]
				: undefined;
			if (staffingError) {
				setStaffingErrorMessage(staffingErrorMessagesByRule[staffingError.rule] ?? staffingError.message);
			} else {
				console.log(error);
				setServerErrorMessage(t("form:unexpectedError"));
			}
		}

		setIsBusy(false);
	};

	const handleFormSwitch = useCallback((newFormId: TimeTrackingFormNamesEnum | null) => {
		setCurrentTimeTrackingFormValues(getValues());
		setCurrentFormName(newFormId);
	}, [getValues, setCurrentFormName, setCurrentTimeTrackingFormValues]);

	const dayName = date ? date.toLocaleDateString("de-DE", { weekday: "short" }) : "";
	const formattedDate = date ? `${dayName}, ${formatDateWithGermanMonth(date, false)}` : "";

	return <>
		<SidebarHeader>
			<SidebarHeaderHeadline>
				{selectedTimeTrackingTypeId !== TimeTrackingTypeId.Project ?
					<span>Typ: {selectedTimeTrackingTypeName}</span> :
					<span className="flex items-center gap-x-2">{avatarImage && <CompanyLogo image={avatarImage}
																							 sizeClassName="size-8" />}
						{selectedStaffingData?.phaseTitle}</span>}
			</SidebarHeaderHeadline>
		</SidebarHeader>
		<SidebarFooter position="top">
			{(!isUpdateForm || selectedTimeTrackingTypeId === TimeTrackingTypeId.Project && selectedStaffingData?.isActive) &&
				<Button className="ml-[-3px] mr-auto text-xs"
						disabled={isBusy}
						size="auto"
						theme="none"
						onClick={() => handleFormSwitch(null)}>
					<ChevronLeftIcon className="size-6" />{isUpdateForm ? "Projektauswahl" : "Zurück"}

				</Button>}
			<SubmitButton formName={TimeTrackingFormNamesEnum.TIME_TRACKING_FORM}
						  size="sm"
						  busy={isBusy}
						  disabled={!isValid || !!quotaLock || quotaBudgetIsExceeded}>
				{!isUpdateForm ? t(normalizeTKey("form:buttonTexts.save")) : t(normalizeTKey("form:buttonTexts.update"))}
			</SubmitButton>
			<Button theme="none"
					size="sm"
					disabled={isBusy}
					onClick={onSuccess}>
				{t(normalizeTKey("form:buttonTexts.cancel"))}
			</Button>
		</SidebarFooter>
		<div className="mb-2 flex w-full justify-between px-7 pt-2 text-xs font-bold text-gray-500">
			<span>{selectedTimeTrackingTypeId === TimeTrackingTypeId.Project ? selectedStaffingData?.orderTitle : ""}</span>
			<span>{formattedDate}</span>
		</div>
		<SidebarContent>
			{serverErrorMessage &&
				<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
			{isBusy && <SidebarBusyOverlay />}
			<form id={TimeTrackingFormNamesEnum.TIME_TRACKING_FORM}
				  onSubmit={handleSubmit(onSubmit)}
				  onKeyDown={preventSubmitOnEnter}
				  className="flex min-h-full w-full flex-col justify-start px-6"
			>
				{staffingErrorMessage && <div className="mb-6">
					<Hint theme="error"
						  size="sm">
						<p className="whitespace-pre-wrap break-words">{staffingErrorMessage}</p>
					</Hint>
				</div>}
				{quotaLock && <div className="mb-6">
					<Hint theme="warning"
						  size="sm">
						<p className="whitespace-pre-wrap break-words">Diese Erfassung ist auf das
							Kontingent{quotaLock.quotaTitle && ` "${quotaLock.quotaTitle}"`} gebucht. Das Kontingent ist
							für diese Erfassung nicht mehr aktiv, daher kann sie nicht bearbeitet werden. Löschen ist
							weiterhin möglich.</p>
					</Hint>
				</div>}
				<TimeTrackingFormSection
					availableDeliverables={availableDeliverables}
					availableLocations={availableLocations}
					control={control}
					errors={errors}
					isEditForm={isUpdateForm}
					isSubmitted={isSubmitted}
					locations={locations}
					quotaBookingOptions={quotaBookingOptions}
					quotaDrawCents={quotaDrawCents}
					quotaLock={quotaLock}
					selectedStaffingData={selectedStaffingData}
					selectedTimeTrackingTypeId={selectedTimeTrackingTypeId}
					setCurrentFormName={handleFormSwitch}
					setValue={setValue}
					selectedUserId={selectedUserId}
					trigger={trigger}
					users={users}
					watch={watch} />
			</form>
		</SidebarContent>
	</>;
};

export default CreateOrUpdateTimeTrackingForm;